<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LeadAlertsAPI from 'dashboard/api/leadAlerts';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useTracking } from 'dashboard/composables/useTracking';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import AlertsManagementToast from './AlertsManagementToast.vue';
import SimplifiedAlertCard from './SimplifiedAlertCard.vue';

const props = defineProps({
  highlightAlertId: { type: [String, Number], default: null },
});

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { t, locale } = useI18n();
const { track } = useTracking();

const alerts = ref([]);
const meta = ref({ count: 0 });
const loading = ref(false);
const loadError = ref(false);
const actionAlertId = ref(null);
const previewAlertId = ref(null);
const previewLoadingId = ref(null);
const previewItems = ref([]);
const previewCount = ref(0);
const pendingDeletion = ref(null);
const showDeleteDialog = ref(false);
const highlightHandled = ref(false);
const applyingPreferences = ref(false);
const toast = ref({
  show: false,
  variant: 'success',
  title: '',
  message: '',
  actionLabel: '',
  action: null,
});

const allowedStatuses = ['active', 'paused', 'archived'];
const statusFilter = ref(
  allowedStatuses.includes(route.query.alert_status)
    ? route.query.alert_status
    : 'all'
);
const page = ref(Math.max(Number(route.query.alert_page) || 1, 1));
const bulkPreferences = reactive({
  delivery_channels: ['whatsapp'],
  delivery_frequency: 'daily',
  delivery_note: '',
});

const formattedLocale = computed(() =>
  (locale.value || 'pt_BR').replace('_', '-')
);

const currentPreviewItems = computed(() =>
  previewAlertId.value ? previewItems.value : []
);

const currentPreviewCount = computed(() =>
  previewAlertId.value ? previewCount.value : 0
);

const hasPrevPage = computed(() => page.value > 1);
const hasNextPage = computed(() => {
  const total = Number(meta.value.count || 0);
  return page.value * 25 < total;
});

const filterChips = computed(() => [
  { value: 'all', label: t('LEAD_ALERTS.FILTERS.ALL') },
  { value: 'active', label: t('LEAD_ALERTS.FILTERS.ACTIVE') },
  { value: 'paused', label: t('LEAD_ALERTS.FILTERS.PAUSED') },
  { value: 'archived', label: t('LEAD_ALERTS.FILTERS.ARCHIVED') },
]);

const emptyStateMessage = computed(() => {
  if (statusFilter.value === 'active') {
    return t('LEAD_ALERTS.EMPTY.ACTIVE');
  }

  if (statusFilter.value === 'paused') {
    return t('LEAD_ALERTS.EMPTY.PAUSED');
  }

  if (statusFilter.value === 'archived') {
    return t('LEAD_ALERTS.EMPTY.ARCHIVED');
  }

  return t('LEAD_ALERTS.EMPTY.ALL');
});

const deleteDescription = computed(() => {
  if (!pendingDeletion.value) {
    return t('LEAD_ALERTS.DELETE.DEFAULT_DESCRIPTION');
  }

  return t('LEAD_ALERTS.DELETE.DESCRIPTION', {
    title: pendingDeletion.value.title,
  });
});

const hydrateBulkPreferences = alert => {
  if (!alert) return;

  bulkPreferences.delivery_channels = [
    ...(alert.delivery_channels || ['whatsapp']),
  ];
  bulkPreferences.delivery_frequency = alert.delivery_frequency || 'daily';
  bulkPreferences.delivery_note = alert.delivery_note || '';
};

const syncQuery = () => {
  const query = { ...route.query, tab: 'alertas' };

  if (statusFilter.value !== 'all') {
    query.alert_status = statusFilter.value;
  } else {
    delete query.alert_status;
  }

  if (page.value > 1) {
    query.alert_page = String(page.value);
  } else {
    delete query.alert_page;
  }

  router.replace({ query }).catch(() => {});
};

const setToast = ({
  variant = 'success',
  title,
  message,
  actionLabel = '',
  action = null,
}) => {
  toast.value = {
    show: true,
    variant,
    title,
    message,
    actionLabel,
    action,
  };
};

const closeToast = () => {
  toast.value = {
    show: false,
    variant: 'success',
    title: '',
    message: '',
    actionLabel: '',
    action: null,
  };
};

const filtersSummary = alert => {
  const formatCurrency = value => {
    if (value == null || value === '') return null;

    return new Intl.NumberFormat(formattedLocale.value, {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(Number(value));
  };

  const parts = [
    alert.finalidade &&
      t('LEAD_ALERTS.FILTER_SUMMARY.PURPOSE', { value: alert.finalidade }),
    alert.cidade,
    alert.bairro,
    alert.tipo,
    alert.quartos_min &&
      t('LEAD_ALERTS.FILTER_SUMMARY.BEDROOMS_MIN', {
        count: alert.quartos_min,
      }),
    alert.preco_min &&
      t('LEAD_ALERTS.FILTER_SUMMARY.PRICE_FROM', {
        value: formatCurrency(alert.preco_min),
      }),
    alert.preco_max &&
      t('LEAD_ALERTS.FILTER_SUMMARY.PRICE_TO', {
        value: formatCurrency(alert.preco_max),
      }),
  ].filter(Boolean);

  return parts.length
    ? parts.join(' · ')
    : t('LEAD_ALERTS.FILTER_SUMMARY.EMPTY');
};

const loadAlerts = async () => {
  loading.value = true;
  try {
    const { data } = await LeadAlertsAPI.list({
      status: statusFilter.value === 'all' ? undefined : statusFilter.value,
      page: page.value,
    });

    alerts.value = data.data || [];
    meta.value = data.meta || { count: 0 };
    loadError.value = false;
    if (alerts.value.length) {
      hydrateBulkPreferences(alerts.value[0]);
    }
  } catch (error) {
    alerts.value = [];
    meta.value = { count: 0 };
    loadError.value = true;
    useAlert(
      error.response?.data?.errors?.[0] ||
        t('LEAD_ALERTS.TOAST.PREFERENCES_ERROR_MESSAGE')
    );
  } finally {
    loading.value = false;
  }
};

const changeFilter = nextFilter => {
  statusFilter.value = nextFilter;
  page.value = 1;
  previewAlertId.value = null;
  previewItems.value = [];
  previewCount.value = 0;
};

const changePage = nextPage => {
  page.value = Math.max(nextPage, 1);
};

const openLead = alert => {
  if (!alert?.contact_id) return;

  router.push({
    name: 'lais_lead_historico',
    params: { accountId: accountId.value, id: alert.contact_id },
  });
};

const openImovel = (alert, imovel) => {
  if (!imovel?.id) return;

  router.push({
    name: 'imovel_show',
    params: { accountId: accountId.value, id: imovel.id },
    query: alert?.contact_id ? { contact_id: String(alert.contact_id) } : {},
  });
};

const openConsentCenter = () => {
  router.push({
    name: 'minha_lais_index',
    params: { accountId: accountId.value },
    query: { tab: 'notificacoes' },
  });
};

const toggleBulkChannel = channel => {
  if (bulkPreferences.delivery_channels.includes(channel)) {
    bulkPreferences.delivery_channels =
      bulkPreferences.delivery_channels.filter(value => value !== channel);
    return;
  }

  bulkPreferences.delivery_channels = [
    ...bulkPreferences.delivery_channels,
    channel,
  ];
};

async function togglePreview(alert, forceOpen = false) {
  if (!alert?.id) return;

  if (!forceOpen && previewAlertId.value === alert.id) {
    previewAlertId.value = null;
    previewItems.value = [];
    previewCount.value = 0;
    return;
  }

  previewLoadingId.value = alert.id;
  try {
    const { data } = await LeadAlertsAPI.matches(alert.id);
    previewAlertId.value = alert.id;
    previewItems.value = data.data || [];
    previewCount.value = Number(data.count || 0);
    track(ACCOUNT_EVENTS.LEAD_ALERT_PREVIEW_OPENED, {
      alertId: alert.id,
      matchesCount: previewCount.value,
      source: 'notifications',
    });
  } catch (error) {
    setToast({
      variant: 'danger',
      title: t('LEAD_ALERTS.TOAST.PREVIEW_ERROR_TITLE'),
      message:
        error.response?.data?.errors?.[0] ||
        t('LEAD_ALERTS.TOAST.PREVIEW_ERROR_MESSAGE'),
      actionLabel: t('LEAD_ALERTS.TOAST.TRY_AGAIN'),
      action: () => togglePreview(alert, true),
    });
  } finally {
    previewLoadingId.value = null;
  }
}

const ensureHighlightedAlert = async () => {
  const highlightId = Number(props.highlightAlertId);
  if (!highlightId || highlightHandled.value) return;

  const alert = alerts.value.find(
    currentAlert => currentAlert.id === highlightId
  );
  if (!alert) return;

  highlightHandled.value = true;
  await togglePreview(alert, true);
};

const applyPreferencesToVisibleAlerts = async () => {
  if (!alerts.value.length) return;
  if (!bulkPreferences.delivery_channels.length) {
    useAlert(t('LEAD_ALERTS.MANAGEMENT.CHANNEL_REQUIRED'));
    return;
  }

  applyingPreferences.value = true;
  try {
    await Promise.all(
      alerts.value.map(alert =>
        LeadAlertsAPI.update(alert.id, {
          filters: {
            delivery_channels: bulkPreferences.delivery_channels,
            delivery_frequency: bulkPreferences.delivery_frequency,
            delivery_note: bulkPreferences.delivery_note,
          },
        })
      )
    );

    await loadAlerts();
    track(ACCOUNT_EVENTS.LEAD_ALERT_PREFERENCES_APPLIED, {
      count: alerts.value.length,
      channels: bulkPreferences.delivery_channels.join(','),
      frequency: bulkPreferences.delivery_frequency,
      source: 'notifications',
    });
    setToast({
      title: t('LEAD_ALERTS.TOAST.PREFERENCES_SUCCESS_TITLE'),
      message: t('LEAD_ALERTS.TOAST.PREFERENCES_SUCCESS_MESSAGE'),
    });
  } catch (error) {
    setToast({
      variant: 'danger',
      title: t('LEAD_ALERTS.TOAST.PREFERENCES_ERROR_TITLE'),
      message:
        error.response?.data?.errors?.[0] ||
        t('LEAD_ALERTS.TOAST.PREFERENCES_ERROR_MESSAGE'),
      actionLabel: t('LEAD_ALERTS.TOAST.TRY_AGAIN'),
      action: applyPreferencesToVisibleAlerts,
    });
  } finally {
    applyingPreferences.value = false;
  }
};

const toggleStatus = async alert => {
  if (!alert?.id) return;

  actionAlertId.value = alert.id;
  const nextStatus = alert.status === 'active' ? 'paused' : 'active';

  try {
    await LeadAlertsAPI.update(alert.id, { status: nextStatus });
    await loadAlerts();
    track(ACCOUNT_EVENTS.LEAD_ALERT_STATUS_CHANGED, {
      alertId: alert.id,
      status: nextStatus,
      source: 'notifications',
    });
    setToast({
      title:
        nextStatus === 'active'
          ? t('LEAD_ALERTS.TOAST.STATUS_ACTIVE_TITLE')
          : t('LEAD_ALERTS.TOAST.STATUS_PAUSED_TITLE'),
      message:
        nextStatus === 'active'
          ? t('LEAD_ALERTS.TOAST.STATUS_ACTIVE_MESSAGE')
          : t('LEAD_ALERTS.TOAST.STATUS_PAUSED_MESSAGE'),
      actionLabel: t('LEAD_ALERTS.TOAST.OPEN_LEAD'),
      action: () => openLead(alert),
    });
  } catch (error) {
    setToast({
      variant: 'danger',
      title: t('LEAD_ALERTS.TOAST.STATUS_ERROR_TITLE'),
      message:
        error.response?.data?.errors?.[0] ||
        t('LEAD_ALERTS.TOAST.STATUS_ERROR_MESSAGE'),
      actionLabel: t('LEAD_ALERTS.TOAST.TRY_AGAIN'),
      action: () => toggleStatus(alert),
    });
  } finally {
    actionAlertId.value = null;
  }
};

const requestDelete = alert => {
  pendingDeletion.value = alert;
  showDeleteDialog.value = true;
};

const resetDeleteDialog = () => {
  pendingDeletion.value = null;
  showDeleteDialog.value = false;
};

const closeDeleteDialog = () => {
  if (actionAlertId.value) return;

  resetDeleteDialog();
};

const deleteAlert = async () => {
  if (!pendingDeletion.value) return;

  const alert = pendingDeletion.value;
  actionAlertId.value = alert.id;

  try {
    await LeadAlertsAPI.destroy(alert.id);
    if (previewAlertId.value === alert.id) {
      previewAlertId.value = null;
      previewItems.value = [];
      previewCount.value = 0;
    }
    resetDeleteDialog();
    await loadAlerts();
    track(ACCOUNT_EVENTS.LEAD_ALERT_DELETED, {
      alertId: alert.id,
      source: 'notifications',
    });
    setToast({
      title: t('LEAD_ALERTS.TOAST.DELETE_SUCCESS_TITLE'),
      message: t('LEAD_ALERTS.TOAST.DELETE_SUCCESS_MESSAGE'),
      actionLabel: t('LEAD_ALERTS.TOAST.OPEN_LEAD'),
      action: () => openLead(alert),
    });
  } catch (error) {
    setToast({
      variant: 'danger',
      title: t('LEAD_ALERTS.TOAST.DELETE_ERROR_TITLE'),
      message:
        error.response?.data?.errors?.[0] ||
        t('LEAD_ALERTS.TOAST.DELETE_ERROR_MESSAGE'),
      actionLabel: t('LEAD_ALERTS.TOAST.TRY_AGAIN'),
      action: deleteAlert,
    });
  } finally {
    actionAlertId.value = null;
  }
};

watch(
  () => [statusFilter.value, page.value],
  async () => {
    syncQuery();
    await loadAlerts();
    await ensureHighlightedAlert();
  },
  { immediate: true }
);

watch(
  () => props.highlightAlertId,
  async () => {
    highlightHandled.value = false;
    await ensureHighlightedAlert();
  }
);
</script>

<template>
  <div class="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_320px]">
    <div class="grid gap-4">
      <AlertsManagementToast
        :show="toast.show"
        :variant="toast.variant"
        :title="toast.title"
        :message="toast.message"
        :action-label="toast.actionLabel"
        @close="closeToast"
        @action="toast.action?.()"
      />

      <LaisCard :title="t('LEAD_ALERTS.MANAGEMENT.TITLE')">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-[13px] leading-6 text-lais-text-muted">
            {{ t('LEAD_ALERTS.MANAGEMENT.HELPER') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="chip in filterChips"
              :key="chip.value"
              type="button"
              class="rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all"
              :class="
                statusFilter === chip.value
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
              "
              @click="changeFilter(chip.value)"
            >
              {{ chip.label }}
            </button>
          </div>
        </div>

        <div v-if="loading" class="py-8 text-[13px] text-lais-text-muted">
          {{ t('LEAD_ALERTS.MANAGEMENT.LOADING') }}
        </div>

        <div
          v-else-if="loadError"
          class="space-y-4 py-8 text-[13px] leading-6 text-lais-text-muted"
        >
          <p>
            {{ t('LEAD_ALERTS.MANAGEMENT.ERROR_COPY') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadAlerts">
              {{ t('LEAD_ALERTS.MANAGEMENT.RELOAD') }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-settings-2"
              @click="openConsentCenter"
            >
              {{ t('LEAD_ALERTS.MANAGEMENT.OPEN_CLIENT_PREFERENCES') }}
            </LaisPrimaryButton>
          </div>
        </div>

        <div
          v-else-if="alerts.length === 0"
          class="py-8 text-[13px] leading-6 text-lais-text-muted"
        >
          {{ emptyStateMessage }}
        </div>

        <div v-else class="grid gap-4">
          <SimplifiedAlertCard
            v-for="alert in alerts"
            :key="alert.id"
            :alert="alert"
            :filters-summary="filtersSummary(alert)"
            :is-working="actionAlertId === alert.id"
            :is-preview-loading="previewLoadingId === alert.id"
            :preview-open="previewAlertId === alert.id"
            :preview-items="
              previewAlertId === alert.id ? currentPreviewItems : []
            "
            :preview-count="
              previewAlertId === alert.id ? currentPreviewCount : 0
            "
            @open-lead="openLead(alert)"
            @toggle-preview="togglePreview(alert)"
            @toggle-status="toggleStatus(alert)"
            @delete="requestDelete(alert)"
            @open-imovel="item => openImovel(alert, item)"
          />
        </div>

        <template #footer>
          <div class="flex items-center justify-between gap-3">
            <p class="text-[12px] text-lais-text-muted">
              {{
                t('LEAD_ALERTS.MANAGEMENT.COUNT', {
                  count: meta.count || alerts.length,
                })
              }}
            </p>
            <div class="flex gap-2">
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-chevron-left"
                :disabled="!hasPrevPage"
                @click="changePage(page - 1)"
              >
                {{ t('LEAD_ALERTS.MANAGEMENT.PREVIOUS') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-chevron-right"
                :disabled="!hasNextPage"
                @click="changePage(page + 1)"
              >
                {{ t('LEAD_ALERTS.MANAGEMENT.NEXT') }}
              </LaisPrimaryButton>
            </div>
          </div>
        </template>
      </LaisCard>
    </div>

    <div class="grid gap-4">
      <LaisCard :title="t('LEAD_ALERTS.MANAGEMENT.EDIT_TITLE')">
        <div class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
          <p>
            {{ t('LEAD_ALERTS.MANAGEMENT.EDIT_HELPER') }}
          </p>

          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all"
              :class="
                bulkPreferences.delivery_channels.includes('whatsapp')
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text'
              "
              @click="toggleBulkChannel('whatsapp')"
            >
              {{ t('LEAD_ALERTS.DELIVERY.WHATSAPP') }}
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all"
              :class="
                bulkPreferences.delivery_channels.includes('sms')
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text'
              "
              @click="toggleBulkChannel('sms')"
            >
              {{ t('LEAD_ALERTS.DELIVERY.SMS') }}
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all"
              :class="
                bulkPreferences.delivery_channels.includes('email')
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text'
              "
              @click="toggleBulkChannel('email')"
            >
              {{ t('LEAD_ALERTS.DELIVERY.EMAIL') }}
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all"
              :class="
                bulkPreferences.delivery_channels.includes('app')
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text'
              "
              @click="toggleBulkChannel('app')"
            >
              {{ t('LEAD_ALERTS.DELIVERY.APP') }}
            </button>
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all"
              :class="
                bulkPreferences.delivery_frequency === 'always'
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text'
              "
              @click="bulkPreferences.delivery_frequency = 'always'"
            >
              {{ t('LEAD_ALERTS.DELIVERY.ALWAYS') }}
            </button>
            <button
              type="button"
              class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all"
              :class="
                bulkPreferences.delivery_frequency === 'daily'
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text'
              "
              @click="bulkPreferences.delivery_frequency = 'daily'"
            >
              {{ t('LEAD_ALERTS.DELIVERY.DAILY') }}
            </button>
          </div>

          <input
            v-model="bulkPreferences.delivery_note"
            type="text"
            maxlength="160"
            class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[13px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
            :placeholder="t('LEAD_ALERTS.MANAGEMENT.NOTE_PLACEHOLDER')"
          />

          <LaisPrimaryButton
            icon="i-lucide-save"
            :is-loading="applyingPreferences"
            :disabled="!alerts.length"
            @click="applyPreferencesToVisibleAlerts"
          >
            {{ t('LEAD_ALERTS.MANAGEMENT.APPLY_CURRENT_PAGE') }}
          </LaisPrimaryButton>

          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-settings-2"
            @click="openConsentCenter"
          >
            {{ t('LEAD_ALERTS.MANAGEMENT.OPEN_CLIENT_PREFERENCES') }}
          </LaisPrimaryButton>
        </div>
      </LaisCard>
    </div>
  </div>

  <LaisConfirmModal
    :show="showDeleteDialog"
    :title="t('LEAD_ALERTS.DELETE.TITLE')"
    :description="deleteDescription"
    :confirm-text="t('LEAD_ALERTS.DELETE.CONFIRM')"
    :is-loading="Boolean(actionAlertId)"
    @close="closeDeleteDialog"
    @confirm="deleteAlert"
  />
</template>
